import { useState, type FormEvent, type ReactNode } from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import {
  STATEMENT_ITEM_ACTION_INTENT,
  StatementRenderer,
  referenceKey,
} from "@/payroll/ai-script/statement/statement-renderer";
import {
  useGetManagedPackStartOptions,
  useStartManagedPackThread,
} from "@/payroll/pack-chat/manager-query";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { getStatementItemReferences } from "@/payroll/pack-chat/statement-items";
import type { IPackChatThreadDetail } from "@/payroll/pack-chat/types";
import { PackPaneNote } from "./pack-pane-note";
import { SelectedStatementItemPreview } from "./selected-statement-item-preview";

const MAX_MESSAGE_LENGTH = 5000;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack className="w-full" align="start" gap={1}>
    <Text variant="label">{label}</Text>
    {children}
  </Stack>
);

export const ManagerStartThreadForm = ({
  businessId,
  periodStart,
  activeRunId,
  onCancel,
  onStarted,
}: {
  businessId: string | null;
  periodStart: string;
  /** The run whose workbook Pay Breakdown is showing for this period. */
  activeRunId: string | null;
  onCancel: () => void;
  /** The created thread, so the caller can select its technician and open it. */
  onStarted: (thread: IPackChatThreadDetail) => void;
}) => {
  const [technicianId, setTechnicianId] = useState<string>();
  const [technicianSearch, setTechnicianSearch] = useState("");
  const [itemKey, setItemKey] = useState<string>();
  const [body, setBody] = useState("");
  const optionsQuery = useGetManagedPackStartOptions(
    businessId,
    periodStart,
    Boolean(activeRunId),
  );
  const startThread = useStartManagedPackThread(businessId, periodStart);

  // The page owns which completed workbook is active. Start options still carry
  // historical runs because other PackChat reads need them, so this form keeps
  // only technicians who have a pack in the page's active run.
  const options = optionsQuery.data?.start_options ?? [];
  const technicians = activeRunId
    ? options
        .filter((option) =>
          option.ai_script_runs.some((run) => run.id === activeRunId),
        )
        .map((option) => option.technician)
    : [];
  const selectedTechnician = technicians.find(
    (technician) => technician.id === technicianId,
  );

  const statementQuery = useGetAiScriptStatement(businessId, activeRunId, {
    identity: STATEMENT_IDENTITY.technician,
    technicianId: selectedTechnician?.id ?? null,
    enabled: Boolean(activeRunId && selectedTechnician),
  });
  const items = getStatementItemReferences(statementQuery.data?.statement);
  const selectedItem = items.find((item) => referenceKey(item) === itemKey);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTechnician || !activeRunId || !selectedItem || !body.trim())
      return;

    try {
      const response = await startThread.mutateAsync({
        technicianId: selectedTechnician.id,
        runId: activeRunId,
        context: selectedItem,
        body: body.trim(),
      });
      toast.success("Thread started", {
        description: `${selectedTechnician?.name ?? "The technician"} has been notified.`,
      });
      onStarted(response.thread);
    } catch (error) {
      toast.error("Thread not started", {
        description: getErrorMessage(error),
      });
    }
  };

  const unavailable = !activeRunId ? (
    <Empty className={emptyStateVariants({ layout: "inline" })}>
      <EmptyHeader>
        <EmptyDescription>
          There is no active payroll run for this period. Complete a payroll run
          before you start a thread.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  ) : optionsQuery.isError ? (
    <PackChatState
      error={optionsQuery.error}
      title="PayPacks couldn't be loaded"
      body="Try again. If it keeps failing, contact support."
      layout="collection"
      action={
        <Button
          variant="secondary"
          size="sm"
          disabled={optionsQuery.isFetching}
          aria-busy={optionsQuery.isFetching || undefined}
          className="mt-2"
          onClick={() => void optionsQuery.refetch()}
        >
          {optionsQuery.isFetching && (
            <Spinner data-icon="inline-start" aria-hidden />
          )}
          Try again
        </Button>
      }
    />
  ) : !optionsQuery.isLoading && technicians.length === 0 ? (
    <Empty className={emptyStateVariants({ layout: "inline" })}>
      <EmptyHeader>
        <EmptyDescription>
          No PayPack in the active run is visible to you, so there is nobody to
          start a thread with.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  ) : null;

  // Every figure on the pack carries a handle here, which is the opposite of the
  // reading view's rule. There the handles competed with the marks that say where
  // the unanswered questions are; picking one is the whole job of this screen, and
  // there are no marks to drown.
  const pickerPane = !activeRunId ? (
    <PackPaneNote>There is no active PayPack for this period.</PackPaneNote>
  ) : !selectedTechnician ? (
    <PackPaneNote>Choose a technician to see their PayPack.</PackPaneNote>
  ) : statementQuery.isLoading ? (
    <Stack gap={4} align="center" className="w-full py-10">
      <Spinner
        layout="centered"
        role="status"
        aria-label="Loading the PayPack"
        size="sm"
        tone="secondary"
      />
    </Stack>
  ) : statementQuery.isError ? (
    <PackPaneNote destructive>That PayPack couldn't be loaded.</PackPaneNote>
  ) : !statementQuery.data?.statement ? (
    <PackPaneNote>No PayPack for this technician in this run.</PackPaneNote>
  ) : items.length === 0 ? (
    <PackPaneNote>This PayPack has no figures to point at.</PackPaneNote>
  ) : (
    <StatementRenderer
      statement={statementQuery.data.statement}
      showTitle={false}
      itemAction={{
        activeKey: itemKey,
        intent: STATEMENT_ITEM_ACTION_INTENT.startThread,
        onAction: (reference) => setItemKey(referenceKey(reference)),
      }}
    />
  );

  return (
    <form
      onSubmit={submit}
      aria-busy={optionsQuery.isLoading}
      // The pack keeps to the right edge, as it does beside a conversation, and at
      // the same sort of width: stretched to fill the card it was enormous. The
      // fields take about 32rem, which is as wide as a form of four controls wants
      // to be, and centre in what is left.
      className="grid w-full min-w-0 lg:min-h-(--pack-chat-panel-height) lg:grid-cols-[minmax(0,1fr)_minmax(300px,26rem)]"
    >
      {/* The column has the card's stable height from the first loading frame, so
          centring does not change when the pack arrives and makes the grid taller.
          It stays pinned while a long pack scrolls. */}
      <Stack
        align="start"
        gap={4}
        className="mx-auto w-full max-w-lg min-w-0 px-5 py-5 lg:sticky lg:top-0 lg:h-(--pack-chat-panel-height) lg:justify-center lg:self-start"
      >
        <Stack className="w-full" align="start" gap={0}>
          <Text variant="title">Start a thread</Text>
          <Text variant="caption" tone="muted">
            Choose the technician, then click the figure on their PayPack.
          </Text>
        </Stack>

        {unavailable ?? (
          <>
            <Field label="Technician">
              <AutoComplete
                ariaLabel="Technician"
                placeholder={
                  optionsQuery.isLoading
                    ? "Loading technicians…"
                    : "Search technicians"
                }
                emptyMessage="No technician matches."
                selectedValue={selectedTechnician?.id ?? ""}
                searchValue={technicianSearch}
                items={technicians.map((technician) => ({
                  value: technician.id,
                  label: technician.name,
                }))}
                fullWidth
                containScroll
                disabled={optionsQuery.isLoading}
                scrollAreaClassName="max-h-72"
                onSearchValueChange={setTechnicianSearch}
                onSelectedValueChange={(value) => {
                  setTechnicianId(value);
                  setItemKey(undefined);
                }}
              />
            </Field>

            {selectedItem ? (
              <SelectedStatementItemPreview item={selectedItem} />
            ) : (
              <Text
                variant="caption"
                tone="muted"
                className="w-full border-y border-line py-3"
              >
                {items.length > 0
                  ? `No figure picked yet. Click one of the ${items.length} on the pack.`
                  : "No figure picked yet."}
              </Text>
            )}

            <Field label="Message">
              <Textarea
                aria-label="Thread message"
                value={body}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Explain what you're checking on, or what you changed"
                disabled={optionsQuery.isLoading || startThread.isPending}
                className="min-h-24 resize-y pt-2"
                onChange={(event) => setBody(event.target.value)}
              />
            </Field>
          </>
        )}

        <Stack align="start" direction="row" gap={2}>
          <Button
            type="submit"
            size="sm"
            aria-busy={startThread.isPending || undefined}
            disabled={
              startThread.isPending ||
              !selectedTechnician ||
              !activeRunId ||
              !selectedItem ||
              !body.trim()
            }
          >
            {startThread.isPending && (
              <Spinner data-icon="inline-start" aria-hidden />
            )}
            Start thread
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={startThread.isPending}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>

      {/* The pack sits where the pack always sits, on the right, so starting a
          thread looks like reading one. Its own hairline rather than the form's
          padding: the pack is a column of its own and runs to the pane edge. */}
      <Stack
        gap={0}
        align="stretch"
        className="min-w-0 border-t border-line lg:border-t-0 lg:border-l"
      >
        {pickerPane}
      </Stack>
    </form>
  );
};
